import React from "react";
import { FacilityEvent, FacilityEventType, getFacilityEventTypeDisplayName } from "@app/shared/src/models";
import { sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import {
    CollisionIcon,
    LeftPassIcon,
    MPS_TO_MPH,
    RightPassIcon,
    SpeedingIcon,
    Tag,
} from "@app/shared/src/components_v2";

/**
 * How the console reads a FacilityEvent.
 *
 * Same split the cab makes: impending-entry events are collisions unless their zone name carries
 * the left/right marker (the zone-name convention in shared/helpers/zoneNameHelpers). Events only
 * reference a detectionZoneId, so callers that can resolve zone names pass a lookup; without one,
 * every impending entry counts as a collision — the safer misfiling.
 */

export type EventCategory = "collision" | "speeding" | "left" | "right" | "other";

export function categorise(event: FacilityEvent, zoneName?: string): EventCategory {
    if (event.eventType === FacilityEventType.Speeding) return "speeding";
    if (event.eventType === FacilityEventType.ImpendingZoneEntry) {
        const side = sideOfZoneName(zoneName);
        return side ?? "collision";
    }
    return "other";
}

export const CATEGORY_LABEL: Record<EventCategory, string> = {
    collision: "Impending collision",
    speeding: "Speeding",
    left: "Left-side pass",
    right: "Right-side pass",
    other: "Other",
};

export const CATEGORY_COLOR: Record<EventCategory, string> = {
    collision: "var(--s1)",
    speeding: "var(--s2)",
    left: "var(--s3)",
    right: "var(--s4)",
    other: "var(--muted)",
};

const CATEGORY_ICON: Record<EventCategory, React.FC<{ size?: number; className?: string }> | undefined> = {
    collision: CollisionIcon,
    speeding: SpeedingIcon,
    left: LeftPassIcon,
    right: RightPassIcon,
    other: undefined,
};

export function eventLabel(event: FacilityEvent, zoneName?: string): string {
    const c = categorise(event, zoneName);
    if (c === "other") return getFacilityEventTypeDisplayName(event.eventType) ?? "Event";
    return CATEGORY_LABEL[c];
}

/** Severity the report and tables colour by: collision=crit, speeding=warn, the rest inform. */
export function severityTone(category: EventCategory): "crit" | "warn" | "accent" {
    return category === "collision" ? "crit" : category === "speeding" ? "warn" : "accent";
}

/** The event's pill: a telltale glyph plus the label, coloured by severity. */
export const EventTag: React.FC<{ event: FacilityEvent; zoneName?: string }> = ({ event, zoneName }) => {
    const c = categorise(event, zoneName);
    const Icon = CATEGORY_ICON[c];
    const tone = c === "collision" ? "crit" : c === "speeding" ? "warn" : c === "other" ? "mute" : "accent";
    return (
        <Tag tone={tone}>
            {Icon && <Icon size={15} />}
            {eventLabel(event, zoneName)}
        </Tag>
    );
};

/**
 * Where an event stands with a human: unreviewed, confirmed real, or dismissed.
 *
 * Three states rather than a checkbox, because "nobody has looked" and "someone looked and said
 * it was nothing" are different facts, and only the second one should take an event off the list.
 */
/**
 * Has anyone looked at this event yet?
 *
 * Loose null check on purpose. The generated model `Object.assign`s the raw JSON onto itself, so
 * an unreviewed event arrives carrying `reviewedAt: null` rather than leaving the field
 * `undefined` — and a strict `=== undefined` test therefore reports the entire log as reviewed.
 */
export function isReviewed(event: FacilityEvent): boolean {
    return event.reviewedAt != null;
}

export const ReviewMark: React.FC<{ event: FacilityEvent }> = ({ event }) => {
    // Nothing at all when unreviewed: a column of dashes is visual noise down a long log, and the
    // marks that are there should be the thing the eye catches.
    if (!isReviewed(event)) return null;
    return event.isValidAlert === false ? (
        <span title="Reviewed — not a real alert" className="text-[12px] font-semibold text-[var(--muted)]">
            Invalid
        </span>
    ) : (
        <span title="Reviewed — confirmed" className="text-[15px] font-bold text-[var(--safe)]">
            ✓
        </span>
    );
};

/** The same three states as words, for the CSV. */
export function reviewStatusText(event: FacilityEvent): string {
    if (!isReviewed(event)) return "not reviewed";
    return event.isValidAlert === false ? "invalid" : "valid";
}

/**
 * Speed carried on the event itself, when the event records one.
 *
 * `eventValue` does not mean the same thing for every event type — on an impending zone entry it
 * is milliseconds to collision (see eventTimeToCollisionMs), so reading it as m/s there produced
 * confident nonsense: a 50 ms warning rendered as 112 mph. The radar track's own maximumVelocity
 * is a real speed whatever the type, so it is still used when the event has been hydrated with it.
 */
export function eventSpeedMph(event: FacilityEvent): number | undefined {
    const fromValue =
        event.eventType !== FacilityEventType.ImpendingZoneEntry && event.eventValue > 0 ? event.eventValue : undefined;
    const mps = event.target?.maximumVelocity ?? fromValue;
    return mps !== undefined ? Math.round(Math.abs(mps) * MPS_TO_MPH) : undefined;
}

/**
 * How close the vehicle came to hitting the truck, expressed as time, in milliseconds.
 *
 * This is what `eventValue` holds on an impending zone entry: the firmware's collision detector
 * stamps `(time to collision) × 1000` on the event it raises. It is emphatically NOT the threshold
 * the alert was configured to fire at — a vehicle keeps closing after the alarm sounds, and what
 * lands on the record is the margin of the near miss itself. Smaller is worse; a couple of hundred
 * milliseconds is a vehicle that very nearly arrived.
 */
export function eventTimeToCollisionMs(event: FacilityEvent): number | undefined {
    if (event.eventType !== FacilityEventType.ImpendingZoneEntry) return undefined;
    return event.eventValue > 0 ? event.eventValue : undefined;
}

/** Milliseconds at the precision a person reads: "380 ms" close in, "1.4 s" further out. */
export function formatMs(ms: number): string {
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/**
 * The one number a row shows for an event: how near the miss was, or how fast it was going.
 * Which one depends on the event type, so the column header has to name both.
 */
export function eventMeasure(event: FacilityEvent): { text: string; title?: string; severe: boolean } | undefined {
    const ttc = eventTimeToCollisionMs(event);
    if (ttc !== undefined) {
        return {
            text: formatMs(ttc),
            title: "How close the vehicle came to hitting the truck, in time",
            // Under a second from impact is the case a director wants their eye pulled to.
            severe: ttc < 1000,
        };
    }
    const mph = eventSpeedMph(event);
    if (mph === undefined) return undefined;
    return { text: `${mph} mph`, severe: mph > 75 };
}
